import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { api } from './client';
import { nint, nstr, str } from './schemaPrimitives';

/* ---------- Install on real devices and iOS test devices (SPEC decision 28, plan P30) ---------- */

const enc = encodeURIComponent;

export const ArtifactInstall = z.object({
  artifactId: str,
  buildId: str,
  platform: z.enum(['ios', 'android']),
  kind: str,
  installable: z.boolean(),
  reason: nstr,
  message: nstr,
  requiresRegisteredDevice: z.boolean(),
  app: z.object({ name: str, bundleId: nstr, versionName: nstr, buildNumber: nint }),
  installPageUrl: nstr,
  qrUrl: nstr,
  apkUrl: nstr,
  iosManifestUrl: nstr,
  itmsServicesUrl: nstr,
  expiresAt: nstr,
});
export type ArtifactInstall = z.infer<typeof ArtifactInstall>;

export const Device = z.object({
  id: str,
  name: str,
  status: z.enum(['awaiting_device', 'registered']),
  deviceClass: nstr,
  udid: nstr,
  product: nstr,
  osVersion: nstr,
  linkExpiresAt: nstr,
  registeredAt: nstr,
  apple: z.object({
    status: z.enum(['not_connected', 'registered', 'failed']).catch('not_connected'),
    simulated: z.boolean(),
    registrations: z.array(z.object({ issuerId: str, status: str, appleDeviceId: nstr, message: nstr, at: str })),
  }),
  createdAt: str,
});
export type Device = z.infer<typeof Device>;

export const DeviceList = z.object({ data: z.array(Device), limits: z.object({ devicesPerClassPerYear: z.number(), newMembershipDelayHours: z.number() }) });
export type DeviceList = z.infer<typeof DeviceList>;
export const DeviceRegistration = z.object({ device: Device, url: str, expiresAt: str });
export type DeviceRegistration = z.infer<typeof DeviceRegistration>;

export const getArtifactInstall = (artifactId: string) => api('GET', `/artifacts/${enc(artifactId)}/install`, { schema: ArtifactInstall });
export const listDevices = () => api('GET', '/devices', { schema: DeviceList });
export const createDeviceRegistration = (name?: string) => api('POST', '/devices/registrations', { schema: DeviceRegistration, body: name ? { name } : {} });
export const removeDevice = (deviceId: string) => api('DELETE', `/devices/${enc(deviceId)}`, { schema: z.null() });

export const installKeys = {
  install: (artifactId: string) => ['artifact', artifactId, 'install'] as const,
  devices: ['devices'] as const,
};

/**
 * The install link lasts 15 minutes and is minted per request: fetched when the sheet opens, never served from cache
 * after it closed (`gcTime: 0`), and "New link" refetches it.
 */
export const useArtifactInstall = (artifactId: string | undefined, enabled: boolean) =>
  useQuery({ queryKey: installKeys.install(artifactId ?? ''), queryFn: () => getArtifactInstall(artifactId ?? ''), enabled: enabled && Boolean(artifactId), staleTime: 10 * 60_000, gcTime: 0, retry: false });

/** The org's devices; while a registration sheet is open, or a row waits for its device, the list refreshes every 5 seconds. */
export const useDevices = (enabled: boolean, sheetOpen: boolean, waiting: (list: DeviceList | undefined) => boolean) =>
  useQuery({ queryKey: installKeys.devices, queryFn: listDevices, enabled, refetchInterval: (q) => (sheetOpen || waiting(q.state.data) ? 5_000 : false) });

export function useDeviceMutations() {
  const qc = useQueryClient();
  const settle = () => void qc.invalidateQueries({ queryKey: installKeys.devices });
  const register = useMutation({ mutationFn: (name?: string) => createDeviceRegistration(name), onSuccess: settle });
  const remove = useMutation({ mutationFn: (deviceId: string) => removeDevice(deviceId), onSuccess: settle });
  return { register, remove };
}
